<script lang="ts">
	import { playWhenVisible } from '../../utils/playWhenVisible/playWhenVisible';
	import { VideoPlayerBase } from '../VideoPlayerBase';

	interface Props {
		/*******************************************************************************************************
		Documentation for each of the video player parameters can be found here:
		Bunny CDN Parameters: https://docs.bunny.net/docs/stream-embedding-videos#parameters
		Youtube Parameters: https://developers.google.com/youtube/player_parameters
		Vimeo Parameters: https://help.vimeo.com/hc/en-us/articles/12426260232977-Player-parameters-overview
	*******************************************************************************************************/
		src: string;
		title: string;
		autoplay?: boolean;
		controls?: boolean;
		playsinline?: boolean;
		loop?: boolean;
		dataCy: string;
		navigating?: boolean | undefined;
		/** Image shown before a video file plays; without one, its first frame is shown. */
		poster?: string;
		class?: string;
		style?: string;
	}

	let {
		src,
		title,
		autoplay = true,
		controls = false,
		playsinline = true,
		loop = true,
		dataCy,
		navigating = undefined,
		poster = undefined,
		...rest
	}: Props = $props();

	let isHeniVideo = $derived(src?.includes('https://nftimages.heni.com'));
	let autoplayParam = $derived(autoplay ? '1' : '0');
	let controlsParam = $derived(controls ? '1' : '0');
	let loopParam = $derived(loop ? '1' : '0');
	let playsinlineParam = $derived(playsinline ? '1' : '0');

	let parsedSrc = $derived(
		(() => {
			if (isHeniVideo) {
				return src;
			}

			if (src?.includes('iframe.mediadelivery.net')) {
				const queryParams = autoplay ? '&autoplay=true&muted=true' : '';
				return `${src}?preload=true&loop=true${queryParams}`;
			}

			if (src?.includes('youtube')) {
				return `https://www.youtube.com/embed/${
					src.split('?v=')[1]
				}?autoplay=${autoplayParam}&mute=${autoplayParam}&controls=${controlsParam}&loop=${loopParam}&playsinline=${playsinlineParam}`;
			}

			const vimeoIds = src ? src.split('vimeo.com')[1]?.split('/') : [];

			if (vimeoIds?.length === 2) {
				return `https://player.vimeo.com/video/${vimeoIds[1]}?title=0&byline=0&portrait=0&autopause=0&autoplay=${autoplayParam}&muted=${autoplayParam}&controls=${controlsParam}&loop=${loopParam}&playsinline=${playsinlineParam}`;
			}

			if (vimeoIds?.length === 3) {
				return `https://player.vimeo.com/video/${vimeoIds[1]}?h=${vimeoIds[2]}&title=0&byline=0&portrait=0&autopause=0&autoplay=${autoplayParam}&muted=${autoplayParam}&controls=${controlsParam}&loop=${loopParam}&playsinline=${playsinlineParam}`;
			}

			return null;
		})()
	);
</script>

{#if parsedSrc}
	{#if parsedSrc !== src}
		<VideoPlayerBase {navigating} {...rest} {dataCy} src={parsedSrc} {title} />
	{:else}
		<!-- Only the metadata and first frame load up front (`#t` makes iOS show that frame); an
		autoplay video starts, muted as browsers require, once it is scrolled into view. -->
		<!-- eslint-disable-next-line svelte/valid-compile -->
		<video
			{title}
			class="h-full"
			preload="metadata"
			muted={autoplay}
			{poster}
			controls
			loop
			playsinline
			{@attach autoplay && playWhenVisible}
			{...rest}
		>
			<source src={poster ? src : `${src}#t=0.1`} type="video/mp4" />
		</video>
	{/if}
{/if}
